import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tab,
  Tabs
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ShareIcon from '@mui/icons-material/Share';
import LanguageIcon from '@mui/icons-material/Language';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
// import QRCode from 'react-qr-code';
import { doc, getDoc, setDoc, Timestamp, collection, addDoc, getDocs, query, orderBy, deleteDoc, updateDoc, where } from 'firebase/firestore';
import { db } from '../../../../firebase';
import axios from 'axios';

interface PublicMembershipLinkProps {
  organizationId: string;
}

// Interface for tracking invitations
interface MemberInvitation {
  id: string;
  email: string;
  invitationDate: Timestamp;
  status: 'pending' | 'accepted' | 'expired';
  lastUpdated: Timestamp;
  customMessage?: string;
}

const PublicMembershipLink: React.FC<PublicMembershipLinkProps> = ({ organizationId }) => {
  const [publicUrl, setPublicUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [generating, setGenerating] = useState<boolean>(false);
  const [showCopiedAlert, setShowCopiedAlert] = useState<boolean>(false);
  const [orgName, setOrgName] = useState<string>('');
  const [showQrCode, setShowQrCode] = useState<boolean>(false);
  const [publicLinkData, setPublicLinkData] = useState<{
    enabled: boolean;
    url: string;
    customUrl?: string;
    pageTitle?: string;
    createdAt?: Timestamp;
    lastUpdated?: Timestamp;
    visits: number;
    conversions: number;
    conversionRate: number;
  }>({
    enabled: false,
    url: '',
    visits: 0,
    conversions: 0,
    conversionRate: 0
  });
  
  // Invitation states
  const [showInviteDialog, setShowInviteDialog] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteMessage, setInviteMessage] = useState<string>('');
  const [sendingInvitation, setSendingInvitation] = useState<boolean>(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [sentInvitations, setSentInvitations] = useState<MemberInvitation[]>([]);
  const [tabValue, setTabValue] = useState<number>(0);
  const [showInviteSuccessAlert, setShowInviteSuccessAlert] = useState<boolean>(false);
  const [loadingInvitations, setLoadingInvitations] = useState<boolean>(false);

  // Fetch organization details and public link data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch organization details
        const orgRef = doc(db, `organizations/${organizationId}`);
        const orgDoc = await getDoc(orgRef);
        
        if (orgDoc.exists()) {
          setOrgName(orgDoc.data().name || '');
        }
        
        // Fetch public link data
        const linkRef = doc(db, `organizations/${organizationId}/settings/publicMembershipPage`);
        const linkDoc = await getDoc(linkRef);
        
        if (linkDoc.exists()) {
          setPublicLinkData({
            ...linkDoc.data(),
            conversionRate: linkDoc.data().visits > 0 
              ? Math.round((linkDoc.data().conversions / linkDoc.data().visits) * 100) 
              : 0
          } as any);
          
          // Set the public URL
          if (linkDoc.data().enabled) {
            if (linkDoc.data().customUrl) {
              setPublicUrl(linkDoc.data().customUrl);
            } else {
              // Default URL format: domain.com/subscribe/[orgId]
              const baseUrl = window.location.origin;
              setPublicUrl(`${baseUrl}/subscribe/${organizationId}`);
            }
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching link data:', err);
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Also fetch sent invitations
    fetchInvitations();
  }, [organizationId]);
  
  // Fetch sent invitations
  const fetchInvitations = async () => {
    try {
      setLoadingInvitations(true);
      
      const invitationsRef = collection(db, `organizations/${organizationId}/memberInvitations`);
      const invitationsQuery = query(invitationsRef, orderBy('invitationDate', 'desc'));
      const invitationsSnapshot = await getDocs(invitationsQuery);
      
      const invitations: MemberInvitation[] = invitationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }) as MemberInvitation);
      
      setSentInvitations(invitations);
      setLoadingInvitations(false);
    } catch (err) {
      console.error('Error fetching invitations:', err);
      setLoadingInvitations(false);
    }
  };
  
  // Generate or update public link
  const generatePublicLink = async () => {
    try {
      setGenerating(true);
      
      // Create default URL if not exists
      const baseUrl = window.location.origin;
      const defaultUrl = `${baseUrl}/subscribe/${organizationId}`;
      
      // Prepare data
      const now = Timestamp.now();
      const linkData = {
        enabled: true,
        url: defaultUrl,
        pageTitle: `${orgName} Membership`,
        createdAt: publicLinkData.createdAt || now,
        lastUpdated: now,
        visits: publicLinkData.visits || 0,
        conversions: publicLinkData.conversions || 0
      };
      
      // Save to Firestore
      const linkRef = doc(db, `organizations/${organizationId}/settings/publicMembershipPage`);
      await setDoc(linkRef, linkData);
      
      // Update state
      setPublicLinkData({
        ...linkData,
        conversionRate: linkData.visits > 0 
          ? Math.round((linkData.conversions / linkData.visits) * 100) 
          : 0
      });
      
      setPublicUrl(defaultUrl);
      setGenerating(false);
    } catch (err) {
      console.error('Error generating public link:', err);
      setGenerating(false);
    }
  };
  
  // Handle invitation dialog
  const openInviteDialog = () => {
    setShowInviteDialog(true);
    setInviteEmail('');
    setInviteMessage('');
    setInviteError(null);
  };
  
  const closeInviteDialog = () => {
    setShowInviteDialog(false);
    setInviteEmail('');
    setInviteMessage('');
    setInviteError(null);
  };
  
  // Handle email input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInviteEmail(e.target.value);
  };
  
  // Handle message input change
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInviteMessage(e.target.value);
  };
  
  // Send invitation email
  const sendInvitation = async () => {
     // Basic validation
     if (!inviteEmail) {
        setInviteError('Please enter an email address');
        return;
     }

     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
     if (!emailRegex.test(inviteEmail)) {
        setInviteError('Please enter a valid email address');
        return;
     }

     try {
        setSendingInvitation(true);
        setInviteError(null);

        // Check if this email was already invited
        const invitationsRef = collection(db, `organizations/${organizationId}/memberInvitations`);
        const existingQuery = query(
           invitationsRef,
           where('email', '==', inviteEmail),
           where('status', '==', 'pending')
        );
        const existingSnapshot = await getDocs(existingQuery);

        if (!existingSnapshot.empty) {
           setInviteError('This email has already been invited');
           setSendingInvitation(false);
           return;
        }

        // Create invitation record in Firestore
        const now = Timestamp.now();
        const invitationData: Omit<MemberInvitation, 'id'> = {
           email: inviteEmail,
           invitationDate: now,
           status: 'pending',
           lastUpdated: now,
           customMessage: inviteMessage || undefined,
        };

        const invitationRef = await addDoc(invitationsRef, invitationData);

        // Prepare email data
        const emailData = {
           to: inviteEmail,
           subject: `You've been invited to join ${orgName} membership`,
           inviterName: orgName,
           eventTitle: `${orgName} Membership Invitation`,
           eventId: organizationId,
           message:
              inviteMessage || `You have been invited to join ${orgName}'s membership program.`,
        };

        // Send invitation email using server endpoint
        await axios.post(
           `${process.env.REACT_APP_API_URL}/api/subscriptions/send-invite`,
           emailData,
           {
              headers: {
                 'Content-Type': 'application/json',
              },
           }
        );

        // Show success alert and close dialog
        setShowInviteSuccessAlert(true);
        closeInviteDialog();

        // Refresh invitations list
        fetchInvitations();
     } catch (err) {
        console.error('Error sending invitation:', err);
        setInviteError('Failed to send invitation. Please try again.');
     } finally {
        setSendingInvitation(false);
     }
  };

  // Delete invitation
  const deleteInvitation = async (invitationId: string) => {
     try {
        const invitationRef = doc(
           db,
           `organizations/${organizationId}/memberInvitations/${invitationId}`
        );
        await deleteDoc(invitationRef);

        // Refresh invitations list
        fetchInvitations();
     } catch (err) {
        console.error('Error deleting invitation:', err);
     }
  };

  // Resend invitation
  const resendInvitation = async (invitation: MemberInvitation) => {
     try {
        // Prepare email data
        const emailData = {
           to: invitation.email,
           subject: `Reminder: You've been invited to join ${orgName} membership`,
           inviterName: orgName,
           eventTitle: `${orgName} Membership Invitation`,
           eventId: organizationId,
           message:
              invitation.customMessage ||
              `You have been invited to join ${orgName}'s membership program.`,
        };

        // Send invitation email using server endpoint
        await axios.post('/api/subscriptions/send-invite', emailData, {
           headers: {
              'Content-Type': 'application/json',
           },
        });

        // Update invitation record
        const invitationRef = doc(
           db,
           `organizations/${organizationId}/memberInvitations/${invitation.id}`
        );
        await updateDoc(invitationRef, {
           lastUpdated: Timestamp.now(),
        });

        // Show success alert
        setShowInviteSuccessAlert(true);

        // Refresh invitations list
        fetchInvitations();
     } catch (err) {
        console.error('Error resending invitation:', err);
     }
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Format timestamp to date string
  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Copy link to clipboard
  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setShowCopiedAlert(true);
  };
  
  // Close copied alert
  const handleCloseAlert = () => {
    setShowCopiedAlert(false);
    setShowInviteSuccessAlert(false);
  };
  
  // Preview the public page
  const previewPage = () => {
    window.open(publicUrl, '_blank');
  };
  
  // Share the link
  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${orgName} Membership`,
          text: `Join ${orgName} membership program for exclusive benefits`,
          url: publicUrl
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      copyLink();
    }
  };
  
  // Download QR code
  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas') as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${orgName.replace(/\s+/g, '-').toLowerCase()}-membership-qr.png`;
      link.href = url;
      link.click();
    }
  };

  return (
     <Box>
        <Paper sx={{ p: 3, mb: 3 }}>
           <Typography variant='h6' component='h3' gutterBottom>
              Public Membership Page
           </Typography>
           <Typography variant='body2' color='text.secondary' paragraph>
              Create a public page where people can subscribe to your organization's membership
              tiers. You can share this link on your website, social media, or email newsletters.
           </Typography>

           {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                 <CircularProgress />
              </Box>
           ) : publicLinkData.enabled ? (
              <Box>
                 {/* Public link info card */}
                 <Card variant='outlined' sx={{ mb: 3 }}>
                    <CardContent>
                       <Box
                          sx={{
                             display: 'flex',
                             justifyContent: 'space-between',
                             alignItems: 'center',
                             mb: 2,
                          }}
                       >
                          <Typography variant='subtitle1' fontWeight='bold'>
                             Your public membership link
                          </Typography>
                          <Button
                             variant='outlined'
                             color='primary'
                             size='small'
                             startIcon={<PersonAddIcon />}
                             onClick={openInviteDialog}
                          >
                             Invite by Email
                          </Button>
                       </Box>
                       <TextField
                          fullWidth
                          value={publicUrl}
                          variant='outlined'
                          size='small'
                          InputProps={{
                             readOnly: true,
                             startAdornment: (
                                <InputAdornment position='start'>
                                   <LinkIcon color='action' />
                                </InputAdornment>
                             ),
                             endAdornment: (
                                <InputAdornment position='end'>
                                   <IconButton onClick={copyLink} size='small' title='Copy link'>
                                      <ContentCopyIcon fontSize='small' />
                                   </IconButton>
                                </InputAdornment>
                             ),
                          }}
                       />

                       <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Button
                             variant='outlined'
                             size='small'
                             startIcon={<LanguageIcon />}
                             onClick={previewPage}
                          >
                             Preview
                          </Button>
                          <Button
                             variant='outlined'
                             size='small'
                             startIcon={<ShareIcon />}
                             onClick={shareLink}
                          >
                             Share
                          </Button>
                          <Button
                             variant='outlined'
                             size='small'
                             startIcon={<QrCodeIcon />}
                             onClick={() => setShowQrCode(true)}
                          >
                             QR Code
                          </Button>
                       </Box>
                    </CardContent>
                 </Card>

                 {/* Tab system for statistics and invitations */}
                 <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                       <Tabs
                          value={tabValue}
                          onChange={handleTabChange}
                          aria-label='membership tabs'
                       >
                          <Tab label='Statistics' />
                          <Tab label='Email Invitations' />
                       </Tabs>
                    </Box>

                    {/* Statistics Tab */}
                    <Box role='tabpanel' hidden={tabValue !== 0} sx={{ py: 2 }}>
                       {tabValue === 0 && (
                          <Grid container spacing={2}>
                             <Grid item xs={12} sm={4}>
                                <Card variant='outlined'>
                                   <CardContent>
                                      <Typography variant='overline' color='text.secondary'>
                                         Page Visits
                                      </Typography>
                                      <Typography variant='h4' component='div'>
                                         {publicLinkData.visits}
                                      </Typography>
                                   </CardContent>
                                </Card>
                             </Grid>
                             <Grid item xs={12} sm={4}>
                                <Card variant='outlined'>
                                   <CardContent>
                                      <Typography variant='overline' color='text.secondary'>
                                         Conversions
                                      </Typography>
                                      <Typography variant='h4' component='div'>
                                         {publicLinkData.conversions}
                                      </Typography>
                                   </CardContent>
                                </Card>
                             </Grid>
                             <Grid item xs={12} sm={4}>
                                <Card variant='outlined'>
                                   <CardContent>
                                      <Typography variant='overline' color='text.secondary'>
                                         Conversion Rate
                                      </Typography>
                                      <Typography variant='h4' component='div'>
                                         {publicLinkData.conversionRate}%
                                      </Typography>
                                   </CardContent>
                                </Card>
                             </Grid>
                          </Grid>
                       )}
                    </Box>

                    {/* Invitations Tab */}
                    <Box role='tabpanel' hidden={tabValue !== 1} sx={{ py: 2 }}>
                       {tabValue === 1 && (
                          <Box>
                             <Box
                                sx={{
                                   display: 'flex',
                                   justifyContent: 'space-between',
                                   alignItems: 'center',
                                   mb: 2,
                                }}
                             >
                                <Typography variant='subtitle1'>Sent invitations</Typography>
                                <Button
                                   variant='contained'
                                   color='primary'
                                   size='small'
                                   startIcon={<EmailIcon />}
                                   onClick={openInviteDialog}
                                >
                                   New Invitation
                                </Button>
                             </Box>

                             {loadingInvitations ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                   <CircularProgress size={24} />
                                </Box>
                             ) : sentInvitations.length > 0 ? (
                                <List sx={{ bgcolor: 'background.paper' }}>
                                   {sentInvitations.map((invitation) => (
                                      <ListItem
                                         key={invitation.id}
                                         divider
                                         secondaryAction={
                                            <Box>
                                               <Tooltip title='Resend invitation'>
                                                  <IconButton
                                                     edge='end'
                                                     aria-label='resend'
                                                     onClick={() => resendInvitation(invitation)}
                                                     sx={{ mr: 1 }}
                                                  >
                                                     <RefreshIcon />
                                                  </IconButton>
                                               </Tooltip>
                                               <Tooltip title='Delete invitation'>
                                                  <IconButton
                                                     edge='end'
                                                     aria-label='delete'
                                                     onClick={() => deleteInvitation(invitation.id)}
                                                  >
                                                     <CloseIcon />
                                                  </IconButton>
                                               </Tooltip>
                                            </Box>
                                         }
                                      >
                                         <ListItemText
                                            primary={invitation.email}
                                            secondary={`Sent: ${formatDate(invitation.invitationDate)} • Status: ${invitation.status}`}
                                         />
                                      </ListItem>
                                   ))}
                                </List>
                             ) : (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                   <Typography variant='body2' color='text.secondary'>
                                      No invitations sent yet
                                   </Typography>
                                   <Button
                                      variant='outlined'
                                      startIcon={<EmailIcon />}
                                      onClick={openInviteDialog}
                                      sx={{ mt: 2 }}
                                   >
                                      Send your first invitation
                                   </Button>
                                </Box>
                             )}
                          </Box>
                       )}
                    </Box>
                 </Box>
              </Box>
           ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                 <Typography variant='body1' paragraph>
                    You don't have a public membership page yet.
                 </Typography>
                 <Button
                    variant='contained'
                    color='primary'
                    onClick={generatePublicLink}
                    disabled={generating}
                    startIcon={generating ? <CircularProgress size={20} /> : <LinkIcon />}
                 >
                    {generating ? 'Generating...' : 'Generate Public Link'}
                 </Button>
              </Box>
           )}
        </Paper>

        {/* QR Code Dialog */}
        <Dialog open={showQrCode} onClose={() => setShowQrCode(false)}>
           <DialogTitle>
              Membership QR Code
              <IconButton
                 aria-label='close'
                 onClick={() => setShowQrCode(false)}
                 sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                 <CloseIcon />
              </IconButton>
           </DialogTitle>
           <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                 {/* <QRCode value={publicUrl} size={200} /> */}
                 <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: 2, textAlign: 'center' }}
                 >
                    Scan this QR code to access the membership page
                 </Typography>
                 <Button
                    variant='outlined'
                    startIcon={<DownloadIcon />}
                    onClick={downloadQRCode}
                    sx={{ mt: 2 }}
                 >
                    Download QR Code
                 </Button>
              </Box>
           </DialogContent>
        </Dialog>

        {/* Invitation Dialog */}
        <Dialog open={showInviteDialog} onClose={closeInviteDialog} maxWidth='sm' fullWidth>
           <DialogTitle>
              Invite Member by Email
              <IconButton
                 aria-label='close'
                 onClick={closeInviteDialog}
                 sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                 <CloseIcon />
              </IconButton>
           </DialogTitle>
           <DialogContent>
              <DialogContentText>
                 Send an email invitation with a link to your membership page. The recipient will
                 receive instructions on how to subscribe.
              </DialogContentText>

              {inviteError && (
                 <Alert severity='error' sx={{ mt: 2, mb: 1 }}>
                    {inviteError}
                 </Alert>
              )}

              <TextField
                 margin='dense'
                 label='Email Address'
                 type='email'
                 fullWidth
                 variant='outlined'
                 value={inviteEmail}
                 onChange={handleEmailChange}
                 required
                 sx={{ mt: 2 }}
              />

              <TextField
                 margin='dense'
                 label='Personal Message (Optional)'
                 multiline
                 rows={4}
                 fullWidth
                 variant='outlined'
                 value={inviteMessage}
                 onChange={handleMessageChange}
                 placeholder='Add a personal message to your invitation...'
                 sx={{ mt: 2 }}
              />
           </DialogContent>
           <DialogActions>
              <Button onClick={closeInviteDialog}>Cancel</Button>
              <Button
                 onClick={sendInvitation}
                 variant='contained'
                 color='primary'
                 disabled={sendingInvitation}
                 startIcon={sendingInvitation ? <CircularProgress size={20} /> : <EmailIcon />}
              >
                 {sendingInvitation ? 'Sending...' : 'Send Invitation'}
              </Button>
           </DialogActions>
        </Dialog>

        {/* Success Alert */}
        <Snackbar
           open={showCopiedAlert}
           autoHideDuration={3000}
           onClose={handleCloseAlert}
           anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
           <Alert onClose={handleCloseAlert} severity='success'>
              Link copied to clipboard!
           </Alert>
        </Snackbar>

        {/* Invitation Success Alert */}
        <Snackbar
           open={showInviteSuccessAlert}
           autoHideDuration={3000}
           onClose={handleCloseAlert}
           anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
           <Alert onClose={handleCloseAlert} severity='success'>
              Invitation sent successfully!
           </Alert>
        </Snackbar>
     </Box>
  );
};

export default PublicMembershipLink;
